import { Accordion, Button, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { FilterClearIcon, ThreeDotsIcon } from '@eleveo/icons-react';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import {
  CheckboxFilterTile,
  acdQueueItems,
  channelTypeItems,
  getCheckboxFilterTileSubtitle,
  historicalDataIntervalItems,
  timezoneItems,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { AcdQueuesResult, SkillsAllResult, TimezonesUsedInQueuesResult } from '../../graphql/types';
import {
  ACTIVE_SIDEBAR_FILTERS_COUNTER,
  APPLY_FILTER_BUTTON_TAG,
  FILTER_SIDEBAR_DROPDOWN_TOGGLE,
  RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
} from './constants';

import type { CheckboxFilterTileProps } from '../../components/LayoutNext/components/CheckboxFilterTile';

export type QueuesSidebarFilterState = {
  acdQueues: string[];
  channelTypes: string[];
  historicalDataIntervals: string[];
  skills: string[];
  timezones: string[];
};

type QueuesFilterSidebarProps = {
  acdQueuesResult: AcdQueuesResult;
  handleCancel: () => void;
  preOpenedItemIds: string[];
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: React.Dispatch<React.SetStateAction<QueuesSidebarFilterState>>;
  sidebarFilter: QueuesSidebarFilterState;
  skillsResult: SkillsAllResult;
  timezoneUsedInQueuesResult: TimezonesUsedInQueuesResult;
};

export const QueuesFilterSidebar: React.FC<QueuesFilterSidebarProps> = ({
  acdQueuesResult,
  handleCancel,
  preOpenedItemIds,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
  skillsResult,
  timezoneUsedInQueuesResult,
}) => {
  const { t } = useTranslation();

  const channelTypes = channelTypeItems(t);
  const acdQueues = acdQueueItems(t, acdQueuesResult);
  const timezones = timezoneItems(timezoneUsedInQueuesResult);
  const historicalDataIntervals = historicalDataIntervalItems(t);
  const skills = skillsResult.data?.skills ?? [];

  const defaultSelectedChannelTypeIds = sidebarFilter.channelTypes;
  const defaultSelectedAcdQueueIds = sidebarFilter.acdQueues;
  const defaultSelectedTimezoneIds = sidebarFilter.timezones;
  const defaultSelectedHistoricalDataIntervalIds = sidebarFilter.historicalDataIntervals;
  const defaultSelectedSkillIds = sidebarFilter.skills;

  // Filters states
  const [selectedChannelTypeIds, setSelectedChannelTypeIds] = useState(defaultSelectedChannelTypeIds);
  const [selectedAcdQueueIds, setSelectedAcdQueueIds] = useState(defaultSelectedAcdQueueIds);
  const [selectedTimezoneIds, setSelectedTimezoneIds] = useState(defaultSelectedTimezoneIds);
  const [selectedHistoricalDataIntervalIds, setSelectedHistoricalDataIntervalIds] = useState(
    defaultSelectedHistoricalDataIntervalIds
  );
  const [selectedSkillIds, setSelectedSkillIds] = useState(defaultSelectedSkillIds);

  const handleApply = () => {
    setSidebarFilter({
      channelTypes: selectedChannelTypeIds,
      acdQueues: selectedAcdQueueIds,
      timezones: selectedTimezoneIds,
      skills: selectedSkillIds,
      historicalDataIntervals: selectedHistoricalDataIntervalIds,
    });

    handleCancel();
  };

  const handleResetAllFilters = () => {
    setSelectedChannelTypeIds([]);
    setSelectedAcdQueueIds([]);
    setSelectedTimezoneIds([]);
    setSelectedHistoricalDataIntervalIds([]);
    setSelectedSkillIds([]);

    setPreOpenedItemIds([]);
  };

  const countOfActiveFilters = [
    !!defaultSelectedChannelTypeIds.length,
    !!defaultSelectedAcdQueueIds.length,
    !!defaultSelectedTimezoneIds.length,
    !!defaultSelectedHistoricalDataIntervalIds.length,
    !!defaultSelectedSkillIds.length,
  ].filter((active) => active).length;

  const isFilterActive =
    selectedChannelTypeIds.length > 0 ||
    selectedAcdQueueIds.length > 0 ||
    selectedTimezoneIds.length > 0 ||
    selectedHistoricalDataIntervalIds.length > 0 ||
    selectedSkillIds.length > 0;

  const isApplyActive =
    equals(selectedChannelTypeIds, defaultSelectedChannelTypeIds) &&
    equals(selectedAcdQueueIds, defaultSelectedAcdQueueIds) &&
    equals(selectedTimezoneIds, defaultSelectedTimezoneIds) &&
    equals(selectedHistoricalDataIntervalIds, defaultSelectedHistoricalDataIntervalIds) &&
    equals(selectedSkillIds, defaultSelectedSkillIds);

  // Accordion filter options
  const filters: CheckboxFilterTileProps[] = [
    {
      testId: 'queues-sidebar-filter-channelTypes',
      id: '1',
      items: channelTypes,
      onSelectionChange: setSelectedChannelTypeIds,
      selectedKeys: selectedChannelTypeIds,
      subtitle: getCheckboxFilterTileSubtitle(t, channelTypes, selectedChannelTypeIds),
      subtitleColor: 'info',
      title: t('queue.channelType.channelTypes'),
    },
    {
      testId: 'queues-sidebar-filter-acdQueues',
      id: '2',
      items: acdQueues,
      onSelectionChange: setSelectedAcdQueueIds,
      selectedKeys: selectedAcdQueueIds,
      sortByField: 'textValue',
      subtitle: getCheckboxFilterTileSubtitle(t, acdQueues, selectedAcdQueueIds),
      subtitleColor: 'info',
      title: t('queue.acdQueues'),
    },
    {
      testId: 'queues-sidebar-filter-timezones',
      id: '3',
      items: timezones,
      onSelectionChange: setSelectedTimezoneIds,
      selectedKeys: selectedTimezoneIds,
      subtitle: getCheckboxFilterTileSubtitle(t, timezones, selectedTimezoneIds),
      subtitleColor: 'info',
      title: t('timezone.timezone', { count: 2 }),
    },
    {
      testId: 'queues-sidebar-filter-data-intervals',
      id: '4',
      items: historicalDataIntervals,
      onSelectionChange: setSelectedHistoricalDataIntervalIds,
      selectedKeys: selectedHistoricalDataIntervalIds,
      sortByField: null,
      subtitle: getCheckboxFilterTileSubtitle(t, historicalDataIntervals, selectedHistoricalDataIntervalIds),
      subtitleColor: 'info',
      title: t('queue.dataInterval', { count: 2 }),
    },
    {
      testId: 'queues-sidebar-filter-skills',
      id: '5',
      items: skills,
      onSelectionChange: (keys) => setSelectedSkillIds([...keys] as string[]),
      selectedKeys: selectedSkillIds,
      subtitle: getCheckboxFilterTileSubtitle(t, skills, selectedSkillIds),
      subtitleColor: 'info',
      title: t('skill.skills'),
    },
  ];

  return (
    <Sidebar onClose={handleCancel} title={t('filter.filters')}>
      <SidebarPanel>
        <Stack direction="horizontal" className="w-full items-center justify-between">
          <Button testId={APPLY_FILTER_BUTTON_TAG} isDisabled={isApplyActive} color="success" onClick={handleApply}>
            {t('form.apply')}
          </Button>
          <Text
            color={!countOfActiveFilters ? { light: 'grey-500', dark: 'grey-300' } : {}}
            testId={ACTIVE_SIDEBAR_FILTERS_COUNTER}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Text>

          <MenuTrigger
            items={[
              {
                id: '1',
                text: t('filter.resetAllFilters'),
                onClick: handleResetAllFilters,
                icon: <FilterClearIcon />,
                isDisabled: !isFilterActive,
                testId: RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
            ]}
          >
            <Button aria-label={t('filter.options')} variant="outline" testId={FILTER_SIDEBAR_DROPDOWN_TOGGLE}>
              <ThreeDotsIcon />
            </Button>
          </MenuTrigger>
        </Stack>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          <Accordion
            expandedKeys={preOpenedItemIds}
            items={filters}
            expansionMode="single"
            onExpandedChange={(keys) => setPreOpenedItemIds([...keys] as string[])}
          >
            {(item) => <CheckboxFilterTile {...item} />}
          </Accordion>
        </SidebarPart>
      </SidebarContent>
    </Sidebar>
  );
};
